/**
 * Etykieta i wartość mocy ładowarki.
 *
 * Sama „MOC" jest dwuznaczna: użytkownik widzi w nazwie produktu jedną liczbę,
 * a w parametrze inną, bez informacji, że obie są prawdziwe i dotyczą różnych
 * rzeczy. Dlatego moc jest ZAWSZE etykietowana typem prądu.
 *
 * Przy stacji hybrydowej brakująca strona jest wypisywana wprost jako
 * „brak danych". Pokazanie samego „DC 120 kW" odtwarzałoby dokładnie tę
 * dwuznaczność, którą ta funkcja usuwa — jawny brak mówi użytkownikowi,
 * że wiemy o istnieniu drugiego parametru i go nie mamy.
 *
 * Jedno miejsce dla listy i karty produktu — inaczej rozjadą się przy
 * pierwszej zmianie.
 */
const BRAK = 'brak danych';

const kw = (v) => (v == null ? null : `${v} kW`);

/**
 * Wiersze mocy do siatki parametrów na karcie produktu.
 *
 * Stacja hybrydowa dostaje DWA osobne wiersze — „Moc DC" i „Moc AC" — zamiast
 * jednej sklejonej wartości. Siatka parametrów jest właśnie od tego, żeby każdy
 * parametr miał własną etykietę; upychanie obu mocy w jedną komórkę robi z nich
 * napis do czytania zamiast dwóch danych do porównania.
 *
 * Kolejność stała: najpierw DC, bo to moc wiodąca i ta z nazwy producenta.
 */
export const wierszeMocy = (product) => {
  const { current_type: prad, power_ac_kw: ac, power_dc_kw: dc } = product || {};
  const display = product?.power_display_kw ?? product?.power_kw;

  if (prad === 'AC_DC') {
    return [
      { label: 'Moc DC', value: dc != null ? kw(dc) : BRAK },
      { label: 'Moc AC', value: ac != null ? kw(ac) : BRAK },
    ];
  }
  if (prad === 'AC') return [{ label: 'Moc AC', value: kw(ac ?? display) }];
  if (prad === 'DC') return [{ label: 'Moc DC', value: kw(dc ?? display) }];
  return [{ label: 'Moc', value: display != null ? kw(display) : 'brak potwierdzonych danych' }];
};

// Wersja jednowierszowa — tam, gdzie jest miejsce tylko na jedną komórkę.
export const etykietaMocy = (product) => {
  const wiersze = wierszeMocy(product);
  if (wiersze.length === 1) return wiersze[0];
  return {
    label: 'Moc',
    value: wiersze.map((w) => `${w.label.replace('Moc ', '')} ${w.value}`).join(' · '),
    hybryda: true,
  };
};

// Skrócona wersja na kafelek listy — tam nie ma miejsca na obie wartości.
export const etykietaMocySkrocona = (product) => {
  const { current_type: prad } = product || {};
  const display = product?.power_display_kw ?? product?.power_kw;
  if (display == null) return '—';
  if (prad === 'AC_DC') return `DC ${display} kW`;
  if (prad === 'DC') return `${display} kW DC`;
  if (prad === 'AC') return `${display} kW AC`;
  return `${display} kW`;
};

export default etykietaMocy;
